import parseSvg from 'html-react-parser/lib';
import { useMemo } from 'react';
import { type TextFragment } from '@/utils/text-kit';
import { TextWritingType, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { TextMarkSVGCreator } from '../utils';

export const useTextMark = (layer: ILayerProps, fragments: TextFragment[]) => {
    const layerData = layer.data as ITextLayerProps;
    const { letterSpacing, fontSize, writingMode, textMark, textContents = [] } = layerData;

    const textContentsStr = JSON.stringify(textContents);
    const textMarkStr = textMark ? JSON.stringify(textMark) : '';

    const markSVG = useMemo(() => {
        if (writingMode !== TextWritingType.HORIZONTAL_TB) return null;
        if (fragments.length === 0) return null;

        // 判断所有分段的 textMark 是否都等于整体的 textMark
        const allScopedTextMarkIsEqualPrimaryTextMark = (() => {
            if (!textMark) return false;
            return fragments.every((f) => JSON.stringify(f.style?.textMark) === textMarkStr);
        })();

        // 处理分段 textMark
        const hasScopedTextMark = fragments.some((f) => f.style?.textMark);
        if (hasScopedTextMark && !allScopedTextMarkIsEqualPrimaryTextMark) {
            // 如果相邻两个 textMark 完全相同，并处于同一行，那么将两个 fragment 合并
            const mergedFragments: TextFragment[] = [];
            for (let i = 0; i < fragments.length; i++) {
                const cur = fragments[i];
                const curTextMark = cur.style?.textMark;
                if (!curTextMark) continue; // 只处理有 textMark 的 fragment

                let prev = i > 0 ? fragments[i - 1] : null;
                const prevTextMark = prev?.style?.textMark;

                if (
                    prev &&
                    prevTextMark &&
                    JSON.stringify(prevTextMark) === JSON.stringify(curTextMark) &&
                    prev.line === cur.line
                ) {
                    if (mergedFragments.length > 0) {
                        const lastMergedIdx = mergedFragments.length - 1;
                        const lastMerged = mergedFragments[lastMergedIdx];
                        if (JSON.stringify(lastMerged.style?.textMark) === JSON.stringify(curTextMark)) {
                            prev = lastMerged;
                        }
                    }

                    const merged = {
                        ...prev,
                        text: prev.text + cur.text,
                        width: prev.width + cur.width,
                        height: Math.max(prev.height, cur.height),
                        top: Math.min(prev.top, cur.top),
                        bottom: Math.max(prev.bottom, cur.bottom),
                        children:
                            prev.children && cur.children
                                ? [...prev.children, ...cur.children]
                                : prev.children || cur.children,
                    };
                    // 替换 mergedFragments 最后一个
                    mergedFragments[mergedFragments.length - 1] = merged;
                } else {
                    mergedFragments.push({ ...cur });
                }
            }

            if (mergedFragments.length > 0) {
                const maxFontSize = mergedFragments.reduce((acc, cur) => {
                    return Math.max(acc, cur.style?.fontSize || fontSize);
                }, fontSize);
                const result = mergedFragments.map((f) => {
                    const svg = TextMarkSVGCreator.create(f.style?.textMark!, f, maxFontSize, letterSpacing);
                    return svg ? parseSvg(svg) : null;
                });
                return result;
            }
        }

        // 处理全局 textMark
        if (textMark) {
            const maxFontSize = fragments.reduce((acc, cur) => {
                return Math.max(acc, cur.style?.fontSize || fontSize);
            }, fontSize);

            // grouped by line number
            const group: Map<number, TextFragment[]> = new Map();
            for (const f of fragments) {
                const line = f.line;
                const list = group.get(line) || [];
                list.push(f);
                group.set(line, list);
            }

            // merge
            const finalFragments: TextFragment[] = [];
            for (const list of group.values()) {
                const minTop = Math.min(...list.map((f) => f.top));
                const maxBottom = Math.max(...list.map((f) => f.bottom));
                const maxHeight = Math.max(...list.map((f) => f.height));
                const merged = list.reduce((acc, cur, idx) => {
                    if (idx === 0) return acc;

                    let next: TextFragment = {
                        ...acc,
                        text: acc.text + cur.text,
                        width: acc.width + cur.width,
                        height: maxHeight,
                        top: minTop,
                        bottom: maxBottom,
                    };
                    if (acc.children && cur.children) next = { ...next, children: [...acc.children, ...cur.children] };
                    return next;
                }, list[0]);
                finalFragments.push(merged);
            }

            const result = finalFragments.map((f) => {
                const svg = TextMarkSVGCreator.create(textMark, f, maxFontSize, letterSpacing);
                return svg ? parseSvg(svg) : null;
            });
            return result;
        }

        return null;
    }, [fragments, textMarkStr, textContentsStr, writingMode, fontSize]);

    return { markSVG };
};
